import React, { FunctionComponent } from "react";
import { Pattern } from "../Pattern";
import Section from "../layout/Section";
import { TitleHeading } from "../typography/TitleHeading";
import BodyText from "../typography/BodyText";
import { useAnimation } from "../../hooks/useAnimate";
import { twMerge } from "tailwind-merge";

type SideGridBlockProps = {
  tag: string;
  title: JSX.Element | string;
  description?: JSX.Element | string;
  blocks: React.ReactNode[];
  theme?: "dark" | "white" | "gray";
  inverse?: boolean;
  singleColumn?: boolean;
  titleBgTag?: boolean;
  half?: boolean;
  children?: React.ReactNode;
  mobileMultiCol?: boolean;
};

const SideGridBlock: FunctionComponent<SideGridBlockProps> = ({
  tag,
  title,
  titleBgTag = false,
  description,
  blocks,
  theme = "white",
  inverse,
  singleColumn,
  half = singleColumn,
  children,
  mobileMultiCol = false,
}) => {
  const { id, animate } = useAnimation();

  return (
    <Section theme={theme} id={id}>
      <div
        className={twMerge(
          "grid grid-cols-1 lg:gap-6",
          half ? "lg:grid-cols-2 xl:gap-12" : "lg:grid-cols-5",
        )}
      >
        <div
          {...animate("fade-right")}
          className={twMerge(
            `z-20 max-w-3xl lg:mb-12 lg:pr-8`,
            inverse && "order-3",
            !half && "lg:col-span-2",
          )}
        >
          <TitleHeading
            theme={theme}
            tag={tag}
            title={title}
            bgTag={titleBgTag}
          />
          {description && (
            <BodyText dark={theme === "dark"} className="mt-4 text-justify">
              {description}
            </BodyText>
          )}
          {children}
        </div>
        <div
          className={twMerge(
            `mt-8 lg:mt-0 order-2 grid grid-cols-1 gap-1`,
            mobileMultiCol && "grid-cols-2",
            !singleColumn && "md:grid-cols-2 ",
            !half && "col-span-3",
          )}
        >
          {blocks.map((block, i) => (
            <div
              {...animate("fade-left", 50 * i)}
              key={`block${i}`}
              className={`${
                theme === "dark"
                  ? "bg-gray-800 hover:bg-gray-700"
                  : theme === "white"
                    ? "bg-gray-100 hover:bg-gray-200"
                    : "bg-gray-200 hover:bg-gray-300"
              } p-6 transition-colors duration-200 z-50`}
            >
              {block}
            </div>
          ))}
        </div>
      </div>
      <Pattern
        type="square"
        className={`hidden lg:block absolute h-1/3 w-1/6 top-0 right-0 ${
          theme === "dark" ? "text-gray-800" : "text-gray-200 opacity-70"
        }`}
      ></Pattern>
      <Pattern
        type="square"
        className={`hidden lg:block absolute h-1/6 xl:h-1/3 w-1/3 bottom-0 left-0 ${
          theme === "dark" ? "text-gray-800" : "text-gray-200 opacity-70"
        }`}
      ></Pattern>
    </Section>
  );
};

export default SideGridBlock;
